import { filter, first, take } from 'rxjs/operators';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { select } from "@angular-redux/store";
import { Observable } from "rxjs";
import { PlanningBlock } from "../../../../pb-screen/models/planning-block/planning-block.interface";
import { TransportOrder } from "../../../../pb-screen/models/transport-order/transport-order.interface";
import { OrderLine } from "../../../../pb-screen/models/order-line/orderLine.interface";
import { SelectionEnum } from "../../../models/selection.enum";
import { CommunicationPartner } from "../../../models/notification/communication-partner.interface";
import { SharedActions } from "../../../shared.actions";
import { HttpClientService } from "../../../services/httpclient";
import { StandardNotification } from "../../../models/notification/standard-notification.interface";
import { NotificationService } from "../../../services/notification.service";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { ToActions } from "../../../../to-screen/to.actions";
import { Notification } from "../../../models/notification/notification.interface";
import { ApplicationUser } from '../../../../pb-screen/models/application.user.interface';
import { SharedService } from "../../../services/shared.service";
import { ToScreenService } from "../../../../pb-screen/services/to-screen.service";
import { SimpleModalComponent } from "ngx-simple-modal";

@Component({
    moduleId: module.id,
    selector: 'notification-modal',
    templateUrl: 'notification-modal.component.html'
})
export class NotificationModalComponent extends SimpleModalComponent<null, null> implements OnInit {
    @ViewChild('modal') public modal: ElementRef;

    @select('selectedPlanningBlocks') private readonly selectedPlanningBlocks$: Observable<PlanningBlock[]>;
    @select('selectedTransportOrders') private readonly selectedTransportOrders$: Observable<TransportOrder[]>;
    @select('selectedOrderlines') private readonly selectedOrderlines$: Observable<OrderLine[]>;
    @select('communicationPartners') private readonly communicationPartners$: Observable<CommunicationPartner[]>;
    @select('standardNotifications') private readonly standardNotifications$: Observable<StandardNotification[]>;
    @select('planningBlockNotifications') public planningBlockNotifications$: Observable<Notification[]>;
    @select('transportOrderNotifications') public transportOrderNotifications$: Observable<Notification[]>;
    @select('applicationUser') public applicationUser$: Observable<ApplicationUser>;

    SelectionEnum = SelectionEnum;

    notificationsToProcess: Notification[] = [];
    processing = false;
    sending = false;

    public enableAutocomplete: boolean = false;
    public activeTab = 0;

    private selectedPlanningBlocks: PlanningBlock[] = [];
    private selectedTransportOrders: TransportOrder[] = [];
    selectedOrderlines: OrderLine[] = [];
    orderLinesOfSelection: { text: string, lineNumber: string, orderNumber: string }[] = [];
    modalSelectedOrderLine: { text: string, lineNumber: string, orderNumber: string };
    selectedItemNotifications: Notification[] = [];
    private selectedRecipient: CommunicationPartner;
    private selectedStandardNotification: StandardNotification;
    subject = "";
    body = "";
    maxCharacters = 250;

    private planningBlockNotifications: Notification[] = [];
    private transportOrderNotifications: Notification[] = [];
    private notifications: Notification[] = [];

    public selectionType: SelectionEnum = SelectionEnum.None;

    private communicationPartners: CommunicationPartner[];
    private standardNotifications: StandardNotification[];
    private firstNotification: Notification;


    constructor(private readonly httpClientService: HttpClientService,
        private readonly notificationService: NotificationService,
        public readonly translate: TranslateService,
        public readonly toastr: ToastrService,
        private readonly sharedActions: SharedActions,
        private readonly toActions: ToActions,
        private toScreenService: ToScreenService,
        private sharedService: SharedService
    ) {
        super();
    }

    private resetModalSelection() {
        this.selectedRecipient = undefined;
        this.selectedStandardNotification = undefined;
        this.subject = "";
        this.body = "";
    }

    ngOnInit(): void {
        this.httpClientService.getToken().pipe(take(1)).subscribe(() => {
            this.sharedActions.getCommunicationPartners();
            this.sharedActions.getStandardNotifications();

            this.applicationUser$.pipe(
                filter(applicationUser => !!applicationUser),
                take(1)
            ).subscribe((applicationUser: ApplicationUser) => {
                this.enableAutocomplete = applicationUser.generalSetting.enableAutocomplete;
            });

            this.communicationPartners$.subscribe((communicationPartners: CommunicationPartner[]) => {
                this.communicationPartners = communicationPartners;
            });

            this.standardNotifications$.subscribe((standardNotifications: StandardNotification[]) => {
                this.standardNotifications = standardNotifications;
            })
        })

        this.planningBlockNotifications$.subscribe((data) => {
            if (data) {
                this.planningBlockNotifications = data;
            }

            this.setSelectedItemNotifications();
        })

        this.transportOrderNotifications$.subscribe((data) => {
            if (data) {
                this.transportOrderNotifications = data;
            }

            this.setSelectedItemNotifications();
        })

        switch (this.selectionType) {
            case SelectionEnum.PlanningBlock:
                this.selectedPlanningBlocks$.pipe(take(1)).subscribe((selection: PlanningBlock[]) => {
                    if (selection?.length > 0) {
                        this.selectedPlanningBlocks = selection;
                        this.sharedService.getOrderLines(selection.map(pb => pb.id).join(","))
                            .pipe(take(1))
                            .subscribe((orderlines: OrderLine[]) => {
                                this.orderLinesOfSelection = orderlines.sort((a, b) => Number(a.sequenceNumber) - Number(b.sequenceNumber))
                                    .map(ol => {
                                        return {
                                            text: `${ol.actionNumber} - ${ol.addressSearchName}`,
                                            lineNumber: ol.lineNumber.toString(),
                                            orderNumber: ol.orderNumber.toString()
                                        };
                                    });

                                this.orderLinesOfSelection.unshift({ text: "", lineNumber: "", orderNumber: "" });
                            }
                            );
                        this.setSelectedItemNotifications();
                    }
                });
                break;
            case SelectionEnum.TransportOrder:
                this.selectedTransportOrders$.pipe(take(1)).subscribe((selection: TransportOrder[]) => {
                    if (selection?.length > 0) {
                        this.selectedTransportOrders = selection;
                        this.toScreenService.getTransportOrderDetails(this.selectedTransportOrders[0].id)
                            .pipe(
                                take(1),
                            ).subscribe((orderlines: OrderLine[]) => {
                                this.orderLinesOfSelection = orderlines.sort((a, b) => Number(a.sequenceNumber) - Number(b.sequenceNumber))
                                    .map(ol => {
                                        return {
                                            text: `${selection[0].id}: ${ol.actionNumber} - ${ol.addressSearchName}`,
                                            lineNumber: ol.lineNumber.toString(),
                                            orderNumber: ol.orderNumber.toString()
                                        };
                                    });

                                this.orderLinesOfSelection.unshift({ text: "", lineNumber: "", orderNumber: "" });
                            }
                            );
                        this.setSelectedItemNotifications();
                    }
                });
                break;
            case SelectionEnum.Orderline:
                //TODO: Hussam Cleanup -  Not sure if that ever happens , but just in case.... 
                this.selectedOrderlines$.pipe(
                    take(1)
                ).subscribe((selection: OrderLine[]) => {
                    if (selection && selection.length > 0) {
                        this.selectedOrderlines = selection;
                    }
                    this.setSelectedItemNotifications();
                });

                break;
        }
    }

    public setSelectedItemNotifications() {
        if (this.selectionType === SelectionEnum.PlanningBlock) {
            if (this.selectedPlanningBlocks.length > 0) {
                this.selectedItemNotifications = this.planningBlockNotifications.filter(x => x.planningBlockId === this.selectedPlanningBlocks[0].id);
                this.firstNotification = this.selectedItemNotifications[0];
            }
        }

        if (this.selectionType === SelectionEnum.TransportOrder) {
            if (this.selectedTransportOrders.length > 0) {
                this.selectedItemNotifications = this.transportOrderNotifications.filter(x => x.transportOrderNumber === this.selectedTransportOrders[0].id);
                this.firstNotification = this.selectedItemNotifications[0];
            }
        }
    }

    public updateRecipient(recipient: CommunicationPartner) {
    }

    public updateStandardNotification(standard: StandardNotification) {
        this.subject = standard.subject;
    }

    public send(): void {
        this.sending = true;

        let itemId = "";
        let orderId = "";
        if (this.selectionType === SelectionEnum.Orderline) {
            itemId = this.selectedOrderlines[0].lineNumber.toString();
            orderId = this.selectedOrderlines[0].orderNumber.toString();
        }
        if (this.selectionType === SelectionEnum.PlanningBlock) {
            itemId = this.selectedPlanningBlocks[0].id.toString();
        }
        if (this.selectionType === SelectionEnum.TransportOrder) {
            itemId = this.selectedTransportOrders[0].id;
        }

        // If we selected an order line internally it takes prio
        if (this.modalSelectedOrderLine?.text) {
            itemId = this.modalSelectedOrderLine.lineNumber;
            orderId = this.modalSelectedOrderLine.orderNumber;
        }

        this.notificationService.createNotification(this.modalSelectedOrderLine ? SelectionEnum.Orderline : this.selectionType, itemId, orderId, this.body, this.subject, this.selectedRecipient.code, this.selectedStandardNotification.code).pipe(take(1)).subscribe(() => {
            this.translateString('TOASTR_MESSAGES.SUCCESS.NOTIFICATION_REGISTERED_SUCCESS').then((result) => {

                if (this.selectionType === SelectionEnum.PlanningBlock) {
                    this.sharedActions.getAllPlanningBlockNotifications();
                    this.selectedPlanningBlocks[0].notifications += 1;
                    this.sharedActions.updatePlanningBlocksDisplay(this.selectedPlanningBlocks);
                }
                if (this.selectionType === SelectionEnum.TransportOrder) {
                    this.sharedActions.getAllTransportOrderNotifications();
                    this.selectedTransportOrders[0].hasNotifications = 1;
                    this.sharedActions.updateTransportOrdersDisplay(this.selectedTransportOrders);
                }
                this.toastr.success(result.toString());
                this.sending = false;
            });
            this.closeModal();
        }, (err) => {
            this.translateString('TOASTR_MESSAGES.ERROR.NOTIFICATION_REGISTERED_ERROR').then((result) => {
                this.toastr.error(result);
                this.sending = false;
            });
        });
    }

    public translateString(text: string) {
        return this.translate.get(text).pipe(first()).toPromise();
    }

    public autocompleListFormatterForRecipients(data: CommunicationPartner): string {
        return data.code;
    }

    public valueListFormatterForRecipients(data: CommunicationPartner): string {
        return data.code;
    }

    public autocompleListFormatterForStandardNotifications(data: StandardNotification): string {
        return data.code;
    }

    public valueListFormatterForStandardNotification(data: StandardNotification): string {
        return data.description;
    }

    public closeModal(): void {
        this.resetModalSelection();
        this.close();
    }

    setActiveTab(number: number) {
        this.activeTab = number;
    }

    processNotifications() {
        this.processing = true;

        const ids = this.notificationsToProcess.map(el => el.id).join(",");

        if (this.selectionType === SelectionEnum.PlanningBlock) {
            this.notificationService.processNotificationsForPb(ids, this.notificationsToProcess[0].planningBlockId).pipe(take(1)).subscribe(result => {
                this.processing = false;
                this.sharedActions.updatePlanningBlocksDisplay([result]);
                this.setNotificationsAsProcessed();
            }, err => {
                this.translateString("TOASTR_MESSAGES.ERROR.NOTIFICATION_PROCESSED_ERROR").then((result) => {
                    this.toastr.error(result);
                    this.processing = false;
                });
            }
            );
        } else if (this.selectionType === SelectionEnum.TransportOrder) {
            this.notificationService.processNotificationsForTo(ids, this.notificationsToProcess[0].transportOrderNumber).pipe(take(1)).subscribe(result => {
                this.processing = false;
                this.toActions.setSelectedTransportOrders([result]);
                this.toActions.updateTransportOrdersDisplay([result]);
                this.setNotificationsAsProcessed();
            }, err => {
                this.translateString("TOASTR_MESSAGES.ERROR.NOTIFICATION_PROCESSED_ERROR").then((result) => {
                    this.toastr.error(result);
                    this.processing = false;
                });
            }
            );
        }
    }

    setNotificationsAsProcessed() {
        this.notificationsToProcess.forEach(el => el.processed = true);
        this.translateString("TOASTR_MESSAGES.SUCCESS.NOTIFICATION_PROCESSED_SUCCESS").then((result) => {
            this.toastr.success(result);
        });
    }

    setSelectionType(selection: { text: string, lineNumber: string, orderNumber: string }) {
        if (!selection.text) {
            this.modalSelectedOrderLine = undefined;
            return;
        }

        this.modalSelectedOrderLine = selection;
    }
}
